import React, { useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableRow,
  IconButton,
  Chip,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import MendocinoLogo from '../../assets/MendocinoFarms_Logo_Horizontal_RGB_FullColor.png';

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
};

const OrderReceiptDialog = ({ open, onClose, order }) => {
  const printRef = useRef();

  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank');

    // Get the logo as base64 for the print window
    const logoImg = document.getElementById('receipt-logo');
    const canvas = document.createElement('canvas');
    canvas.width = logoImg.naturalWidth;
    canvas.height = logoImg.naturalHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(logoImg, 0, 0);
    const logoBase64 = canvas.toDataURL('image/png');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Order Receipt - ${order?.orderRef}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Lato:wght@400;700&display=swap');

            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }

            body {
              font-family: 'Lato', sans-serif;
              font-size: 12px;
              color: #333;
              padding: 24px;
              max-width: 800px;
              margin: 0 auto;
            }

            .receipt-header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              margin-bottom: 20px;
              padding-bottom: 16px;
              border-bottom: 2px solid #333;
            }

            .logo {
              max-width: 280px;
              height: auto;
            }

            .header-info {
              text-align: right;
              font-size: 13px;
            }

            .header-info p {
              margin: 4px 0;
            }

            .header-info .order-ref {
              font-size: 16px;
              font-weight: 700;
            }

            .separator {
              display: flex;
              align-items: center;
              margin: 20px 0;
            }

            .separator hr {
              flex: 1;
              border: none;
              border-top: 1px solid #ccc;
            }

            .separator-text {
              padding: 0 16px;
              font-weight: 700;
              font-size: 14px;
              text-transform: uppercase;
              color: #666;
            }

            .info-container {
              display: flex;
              gap: 20px;
              margin-bottom: 20px;
            }

            .info-box {
              flex: 1;
              border: 1px solid #ddd;
              border-radius: 4px;
              padding: 12px;
            }

            .info-box h3 {
              font-size: 12px;
              text-transform: uppercase;
              color: #666;
              margin-bottom: 8px;
              letter-spacing: 0.5px;
            }

            .info-box p {
              margin: 4px 0;
              font-size: 13px;
            }

            .items-table {
              width: 100%;
              border-collapse: collapse;
              margin: 20px 0;
            }

            .items-table th {
              text-align: left;
              padding: 8px;
              border-bottom: 2px solid #333;
              font-size: 11px;
              text-transform: uppercase;
              color: #666;
            }

            .items-table td {
              padding: 8px;
              border-bottom: 1px solid #eee;
              font-size: 13px;
              vertical-align: top;
            }

            .items-table .item-name {
              font-weight: 500;
            }

            .items-table .modifier {
              font-size: 11px;
              color: #666;
              padding-left: 12px;
            }

            .items-table .special-instructions {
              font-size: 11px;
              color: #b45309;
              font-style: italic;
              padding-left: 12px;
            }

            .items-table .text-right {
              text-align: right;
            }

            .totals-section {
              display: flex;
              justify-content: flex-end;
            }

            .totals-table {
              width: 300px;
              border-collapse: collapse;
            }

            .totals-table td {
              padding: 6px 8px;
              font-size: 13px;
            }

            .totals-table .label {
              color: #666;
            }

            .totals-table .value {
              text-align: right;
            }

            .totals-table .total-row td {
              font-weight: 700;
              font-size: 16px;
              border-top: 2px solid #333;
              padding-top: 12px;
            }

            .totals-table .discount {
              color: #16a34a;
            }

            .coupon-box {
              margin-top: 16px;
              padding: 12px;
              background: #f0fdf4;
              border: 1px dashed #16a34a;
              border-radius: 4px;
            }

            .coupon-box .label {
              font-weight: 600;
              color: #16a34a;
              font-size: 12px;
            }

            .coupon-box .description {
              font-size: 11px;
              color: #666;
            }

            .payment-section {
              margin-top: 20px;
              padding-top: 16px;
              border-top: 1px solid #ddd;
            }

            .payment-section h3 {
              font-size: 12px;
              text-transform: uppercase;
              color: #666;
              margin-bottom: 8px;
            }

            .receipt-footer {
              margin-top: 32px;
              padding-top: 16px;
              border-top: 1px solid #ddd;
              text-align: center;
            }

            .receipt-footer p {
              font-size: 11px;
              color: #666;
              margin: 4px 0;
            }

            .status-chip {
              display: inline-block;
              padding: 2px 8px;
              border-radius: 12px;
              font-size: 11px;
              font-weight: 600;
            }

            .status-closed { background: #dcfce7; color: #166534; }
            .status-placed { background: #dbeafe; color: #1e40af; }
            .status-cancelled { background: #fee2e2; color: #991b1b; }

            @media print {
              body { padding: 0; }
              @page { margin: 0.5in; }
            }
          </style>
        </head>
        <body>
          <div class="receipt-header">
            <img src="${logoBase64}" alt="Mendocino Farms" class="logo" />
            <div class="header-info">
              <p>Order #: <span class="order-ref">${order.orderRef}</span></p>
              <p>Status: <span class="status-chip status-${order.orderStatus?.toLowerCase()}">${order.orderStatus}</span></p>
              <p>Store: <strong>${order.locationName}</strong></p>
              <p>Store #${order.storeNumber}</p>
            </div>
          </div>

          <div class="separator">
            <hr />
            <span class="separator-text">Order Receipt</span>
            <hr />
          </div>

          <div class="info-container">
            <div class="info-box">
              <h3>Order Information</h3>
              <p><strong>Placed:</strong> ${order.timePlaced}</p>
              <p><strong>Wanted:</strong> ${order.timeWanted}</p>
              <p><strong>Method:</strong> ${order.deliveryMethod}</p>
              ${order.guestCount ? `<p><strong>Guests:</strong> ${order.guestCount}</p>` : ''}
            </div>
            ${order.customer ? `
            <div class="info-box">
              <h3>Customer</h3>
              <p><strong>${order.customer.firstName} ${order.customer.lastName}</strong></p>
              ${order.customer.phone ? `<p>${order.customer.phone}</p>` : ''}
              ${order.customer.email ? `<p>${order.customer.email}</p>` : ''}
            </div>
            ` : ''}
            ${order.address ? `
            <div class="info-box">
              <h3>Delivery Address</h3>
              <p>${order.address.streetAddress1}</p>
              ${order.address.streetAddress2 ? `<p>${order.address.streetAddress2}</p>` : ''}
              <p>${order.address.city}, ${order.address.postalCode}</p>
              ${order.address.comments ? `<p style="color: #666; font-style: italic; margin-top: 8px;">Note: ${order.address.comments}</p>` : ''}
            </div>
            ` : ''}
          </div>

          <table class="items-table">
            <thead>
              <tr>
                <th style="width: 50px;">Qty</th>
                <th>Item</th>
                <th style="width: 100px; text-align: right;">Price</th>
              </tr>
            </thead>
            <tbody>
              ${order.items?.map(item => `
                <tr>
                  <td>${item.quantity}</td>
                  <td>
                    <div class="item-name">${item.description}</div>
                    ${item.modifiers?.map(mod => `
                      <div class="modifier">+ ${mod.description}${mod.unitPrice > 0 ? ` (${formatCurrency(mod.unitPrice)})` : ''}</div>
                    `).join('') || ''}
                    ${item.specialInstructions ? `<div class="special-instructions">"${item.specialInstructions}"</div>` : ''}
                  </td>
                  <td class="text-right">${formatCurrency(item.unitPrice * item.quantity)}</td>
                </tr>
              `).join('') || ''}
            </tbody>
          </table>

          <div class="totals-section">
            <table class="totals-table">
              <tr>
                <td class="label">Subtotal</td>
                <td class="value">${formatCurrency(order.subtotal)}</td>
              </tr>
              ${order.discount > 0 ? `
              <tr class="discount">
                <td class="label">Discount</td>
                <td class="value">-${formatCurrency(order.discount)}</td>
              </tr>
              ` : ''}
              <tr>
                <td class="label">Tax</td>
                <td class="value">${formatCurrency(order.salesTax)}</td>
              </tr>
              ${order.deliveryFee > 0 ? `
              <tr>
                <td class="label">Delivery Fee</td>
                <td class="value">${formatCurrency(order.deliveryFee)}</td>
              </tr>
              ` : ''}
              ${order.tip > 0 ? `
              <tr>
                <td class="label">Tip</td>
                <td class="value">${formatCurrency(order.tip)}</td>
              </tr>
              ` : ''}
              <tr class="total-row">
                <td>Total</td>
                <td class="value">${formatCurrency(order.total)}</td>
              </tr>
            </table>
          </div>

          ${order.couponCode ? `
          <div class="coupon-box">
            <div class="label">COUPON APPLIED: ${order.couponCode}</div>
            ${order.couponDescription ? `<div class="description">${order.couponDescription}</div>` : ''}
          </div>
          ` : ''}

          ${order.payments && order.payments.length > 0 ? `
          <div class="payment-section">
            <h3>Payment</h3>
            ${order.payments.map(payment => `
              <p>${payment.paymentType}${payment.panSuffix ? ` ****${payment.panSuffix}` : ''}: ${formatCurrency(payment.amount)}</p>
            `).join('')}
          </div>
          ` : ''}

          <div class="receipt-footer">
            <p><strong>Thank you for your order!</strong></p>
            <p>mendocinofarms.com</p>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  if (!order) return null;

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'closed': return 'success';
      case 'placed': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { maxHeight: '90vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Order Receipt</Typography>
        <Box>
          <IconButton onClick={handlePrint} size="small" sx={{ mr: 1 }}>
            <PrintIcon />
          </IconButton>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3 }}>
        <Box ref={printRef}>
          {/* Header with Logo */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', pb: 2, borderBottom: '2px solid', borderColor: 'text.primary' }}>
            <Box>
              <img
                id="receipt-logo"
                src={MendocinoLogo}
                alt="Mendocino Farms"
                style={{ maxWidth: 250, height: 'auto' }}
                crossOrigin="anonymous"
              />
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2">
                Order #: <strong style={{ fontSize: '1.1rem' }}>{order.orderRef}</strong>
              </Typography>
              <Box sx={{ my: 0.5 }}>
                <Chip
                  label={order.orderStatus}
                  size="small"
                  color={getStatusColor(order.orderStatus)}
                />
              </Box>
              <Typography variant="body2">
                Store: <strong>{order.locationName}</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Store #{order.storeNumber}
              </Typography>
            </Box>
          </Box>

          {/* Separator */}
          <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
            <Divider sx={{ flex: 1 }} />
            <Typography variant="caption" sx={{ px: 2, fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 1 }}>
              Order Receipt
            </Typography>
            <Divider sx={{ flex: 1 }} />
          </Box>

          {/* Info Boxes */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            {/* Order Info */}
            <Box sx={{ flex: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 0.5 }}>
                Order Information
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2"><strong>Placed:</strong> {order.timePlaced}</Typography>
                <Typography variant="body2"><strong>Wanted:</strong> {order.timeWanted}</Typography>
                <Typography variant="body2"><strong>Method:</strong> {order.deliveryMethod}</Typography>
              </Box>
            </Box>

            {/* Customer */}
            {order.customer && (
              <Box sx={{ flex: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 0.5 }}>
                  Customer
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {order.customer.firstName} {order.customer.lastName}
                  </Typography>
                  {order.customer.phone && <Typography variant="body2">{order.customer.phone}</Typography>}
                  {order.customer.email && <Typography variant="body2">{order.customer.email}</Typography>}
                </Box>
              </Box>
            )}

            {/* Delivery Address */}
            {order.address && (
              <Box sx={{ flex: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 0.5 }}>
                  Delivery Address
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2">{order.address.streetAddress1}</Typography>
                  {order.address.streetAddress2 && <Typography variant="body2">{order.address.streetAddress2}</Typography>}
                  <Typography variant="body2">{order.address.city}, {order.address.postalCode}</Typography>
                  {order.address.comments && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                      Note: {order.address.comments}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </Box>

          {/* Items Table */}
          <Table size="small" sx={{ mb: 3 }}>
            <TableBody>
              <TableRow sx={{ '& td': { borderBottom: '2px solid', borderColor: 'text.primary', py: 1 } }}>
                <TableCell sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', fontSize: '0.7rem', width: 50 }}>Qty</TableCell>
                <TableCell sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', fontSize: '0.7rem' }}>Item</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', fontSize: '0.7rem', width: 100 }}>Price</TableCell>
              </TableRow>
              {order.items?.map((item, idx) => (
                <TableRow key={idx} sx={{ '& td': { borderBottom: '1px solid', borderColor: 'divider', py: 1, verticalAlign: 'top' } }}>
                  <TableCell>{item.quantity}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{item.description}</Typography>
                    {item.modifiers?.map((mod, midx) => (
                      <Typography key={midx} variant="caption" display="block" sx={{ pl: 1.5, color: 'text.secondary' }}>
                        + {mod.description}{mod.unitPrice > 0 && ` (${formatCurrency(mod.unitPrice)})`}
                      </Typography>
                    ))}
                    {item.specialInstructions && (
                      <Typography variant="caption" display="block" sx={{ pl: 1.5, color: 'warning.main', fontStyle: 'italic' }}>
                        "{item.specialInstructions}"
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{formatCurrency(item.unitPrice * item.quantity)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Totals */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Table size="small" sx={{ width: 280 }}>
              <TableBody>
                <TableRow sx={{ '& td': { border: 0, py: 0.5 } }}>
                  <TableCell sx={{ color: 'text.secondary' }}>Subtotal</TableCell>
                  <TableCell align="right">{formatCurrency(order.subtotal)}</TableCell>
                </TableRow>
                {order.discount > 0 && (
                  <TableRow sx={{ '& td': { border: 0, py: 0.5, color: 'success.main' } }}>
                    <TableCell>Discount</TableCell>
                    <TableCell align="right">-{formatCurrency(order.discount)}</TableCell>
                  </TableRow>
                )}
                <TableRow sx={{ '& td': { border: 0, py: 0.5 } }}>
                  <TableCell sx={{ color: 'text.secondary' }}>Tax</TableCell>
                  <TableCell align="right">{formatCurrency(order.salesTax)}</TableCell>
                </TableRow>
                {order.deliveryFee > 0 && (
                  <TableRow sx={{ '& td': { border: 0, py: 0.5 } }}>
                    <TableCell sx={{ color: 'text.secondary' }}>Delivery Fee</TableCell>
                    <TableCell align="right">{formatCurrency(order.deliveryFee)}</TableCell>
                  </TableRow>
                )}
                {order.tip > 0 && (
                  <TableRow sx={{ '& td': { border: 0, py: 0.5 } }}>
                    <TableCell sx={{ color: 'text.secondary' }}>Tip</TableCell>
                    <TableCell align="right">{formatCurrency(order.tip)}</TableCell>
                  </TableRow>
                )}
                <TableRow sx={{ '& td': { borderTop: '2px solid', borderColor: 'text.primary', pt: 1.5 } }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1rem' }}>{formatCurrency(order.total)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>

          {/* Coupon */}
          {order.couponCode && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'success.50', borderRadius: 1, border: '1px dashed', borderColor: 'success.main' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                COUPON APPLIED: {order.couponCode}
              </Typography>
              {order.couponDescription && (
                <Typography variant="caption" color="text.secondary">{order.couponDescription}</Typography>
              )}
            </Box>
          )}

          {/* Payment */}
          {order.payments && order.payments.length > 0 && (
            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 0.5 }}>
                Payment
              </Typography>
              <Box sx={{ mt: 1 }}>
                {order.payments.map((payment, idx) => (
                  <Typography key={idx} variant="body2">
                    {payment.paymentType}
                    {payment.panSuffix && <span style={{ color: '#666' }}> ****{payment.panSuffix}</span>}
                    : {formatCurrency(payment.amount)}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          {/* Footer */}
          <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Thank you for your order!</Typography>
            <Typography variant="caption" color="text.secondary">mendocinofarms.com</Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" size="small">
          Close
        </Button>
        <Button onClick={handlePrint} variant="contained" size="small" startIcon={<PrintIcon />}>
          Print Receipt
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderReceiptDialog;
